import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, PhoneOff, PhoneIncoming, ArrowRight, Mail, X } from 'lucide-react';

const MissedCallCalculator = () => {
  // --- State Management ---
  const [inputs, setInputs] = useState({
    totalCallsPerWeek: 50,
    missedCallsPerWeek: 10,
    avgJobValue: 250,
  });

  const [results, setResults] = useState({
    missedCallRate: 0,
    monthlyLost: 0,
    yearlyLost: 0,
    recoveredLow: 0,
    recoveredHigh: 0,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState('idle'); // idle, sending, success

  // --- Calculations ---
  useEffect(() => {
    const { totalCallsPerWeek, missedCallsPerWeek, avgJobValue } = inputs;

    // Prevent division by zero
    const safeTotal = totalCallsPerWeek > 0 ? totalCallsPerWeek : 1;
    
    // Calculate Rate
    const rate = (missedCallsPerWeek / safeTotal) * 100;

    // Calculate Lost Revenue
    const weeklyLost = missedCallsPerWeek * avgJobValue;
    const monthlyLost = weeklyLost * 4.33; // Avg weeks in a month
    const yearlyLost = weeklyLost * 52;

    // Calculate Recovery (20% to 40%)
    const recoveredLow = yearlyLost * 0.20;
    const recoveredHigh = yearlyLost * 0.40;

    setResults({
      missedCallRate: Math.min(rate, 100).toFixed(1),
      monthlyLost,
      yearlyLost,
      recoveredLow,
      recoveredHigh,
    });
  }, [inputs]);

  // --- Handlers ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const numValue = parseFloat(value) || 0;

    setInputs((prev) => {
      // Validation: Missed calls can't exceed total calls
      if (name === 'missedCallsPerWeek' && numValue > prev.totalCallsPerWeek) {
        return { ...prev, [name]: prev.totalCallsPerWeek };
      }
      if (name === 'totalCallsPerWeek' && numValue < prev.missedCallsPerWeek) {
        return { ...prev, [name]: numValue, missedCallsPerWeek: numValue };
      }
      return { ...prev, [name]: numValue };
    });
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    setEmailStatus('sending');

    // SIMULATION: Connect your backend API or EmailJS here
    setTimeout(() => {
      setEmailStatus('success');
      // Reset after 3 seconds
      setTimeout(() => {
        setIsModalOpen(false);
        setEmailStatus('idle');
        setEmail('');
      }, 3000);
    }, 1500);
  };

  // --- Formatting Helper ---
  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(val);
  };

  return (
    <div className="w-full max-w-4xl mx-auto bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden font-sans md:mt-8 lg:mt-12 md:mb-8 lg:mb-12">
      
      {/* Header */}
      <div className="bg-slate-900 p-6 text-white">
        <div className="flex items-center gap-3">
          <div className="bg-blue-500 p-2 rounded-lg">
            <Calculator className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Missed Calls → Lost Revenue Calculator</h2>
            <p className="text-slate-400 text-sm">See how much revenue "My AI Agent" can recover for you.</p>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
        
        {/* LEFT COLUMN: INPUTS */}
        <div className="space-y-8">
          
          {/* Input 1: Total Calls */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <label className="flex items-center gap-2 font-semibold text-gray-700">
                <PhoneIncoming className="w-4 h-4 text-blue-600" />
                Total Calls Per Week
              </label>
              <input
                type="number"
                name="totalCallsPerWeek"
                value={inputs.totalCallsPerWeek}
                onChange={handleInputChange}
                className="w-20 p-1 text-right border rounded-md font-medium text-gray-900 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <input
              type="range"
              name="totalCallsPerWeek"
              min="0"
              max="500"
              value={inputs.totalCallsPerWeek}
              onChange={handleInputChange}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>

          {/* Input 2: Missed Calls */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <label className="flex items-center gap-2 font-semibold text-gray-700">
                <PhoneOff className="w-4 h-4 text-red-500" />
                Missed Calls Per Week
              </label>
              <input
                type="number"
                name="missedCallsPerWeek"
                value={inputs.missedCallsPerWeek}
                onChange={handleInputChange}
                className="w-20 p-1 text-right border rounded-md font-medium text-gray-900 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <input
              type="range"
              name="missedCallsPerWeek"
              min="0"
              max={inputs.totalCallsPerWeek}
              value={inputs.missedCallsPerWeek}
              onChange={handleInputChange}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-500"
            />
            <p className="text-xs text-right text-gray-500">
              You miss <span className="font-bold text-red-600">{results.missedCallRate}%</span> of your calls
            </p>
          </div>

          {/* Input 3: Job Value */}
          <div className="space-y-3">
            <label className="flex items-center gap-2 font-semibold text-gray-700">
              <DollarSign className="w-4 h-4 text-green-600" />
              Average Job / Sale Value
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                name="avgJobValue"
                value={inputs.avgJobValue}
                onChange={handleInputChange}
                className="w-full pl-7 p-3 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500 text-lg font-medium"
              />
            </div>
          </div>

        </div>

        {/* RIGHT COLUMN: RESULTS */}
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 flex flex-col justify-between">
          
          <div className="space-y-6">
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Your Potential Losses</h3>
            
            {/* Monthly Loss */}
            <div className="flex justify-between items-end border-b border-gray-200 pb-4">
              <span className="text-gray-600 font-medium">Monthly Revenue Lost</span>
              <span className="text-2xl font-bold text-gray-800">{formatCurrency(results.monthlyLost)}</span>
            </div>

            {/* Yearly Loss (Hero Metric) */}
            <div className="bg-red-50 border border-red-100 p-4 rounded-lg">
              <span className="text-red-600 font-medium text-sm">Yearly Revenue Lost</span>
              <div className="text-4xl font-extrabold text-red-600 mt-1">
                {formatCurrency(results.yearlyLost)}
              </div>
            </div>
          </div>

          <div className="mt-8 space-y-4">
            <h3 className="text-sm font-bold text-blue-600 uppercase tracking-wider flex items-center gap-2">
              My AI Agent Recovery <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Solution</span>
            </h3>
            
            <p className="text-sm text-gray-600 leading-relaxed">
              Our system immediately engages missed calls via text, recovering 20-40% of these leads.
            </p>

            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-5 rounded-xl text-white shadow-lg">
              <div className="text-blue-100 text-sm font-medium mb-1">Potential Annual Recovered Revenue</div>
              <div className="text-3xl font-bold">
                {formatCurrency(results.recoveredLow)} - {formatCurrency(results.recoveredHigh)}
              </div>
            </div>

            <button 
              onClick={() => setIsModalOpen(true)}
              className="w-full mt-4 group flex items-center justify-center gap-2 bg-white border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white transition-all py-3 px-4 rounded-lg font-bold"
            >
              <Mail className="w-4 h-4" />
              Email Me This Report
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

        </div>
      </div>

      {/* --- EMAIL MODAL --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 relative animate-in fade-in zoom-in duration-200">
            <button 
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>

            {emailStatus === 'success' ? (
              <div className="text-center py-8 space-y-3">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto text-green-600">
                  <ArrowRight className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Report Sent!</h3>
                <p className="text-gray-600">Check your inbox shortly for your custom breakdown.</p>
              </div>
            ) : (
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Get Your Custom Report</h3>
                  <p className="text-gray-500 text-sm mt-1">
                    We'll email you the breakdown of how you can save 
                    <span className="font-bold text-blue-600"> {formatCurrency(results.recoveredLow)}</span> this year.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input 
                    type="email" 
                    required
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <button 
                  type="submit"
                  disabled={emailStatus === 'sending'}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors disabled:opacity-70 flex justify-center items-center gap-2"
                >
                  {emailStatus === 'sending' ? 'Sending...' : 'Send Report'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MissedCallCalculator;